import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { submitLeadB2C } from '@/services/leadService'
import { trackWaitlistSignup } from '@/lib/analytics'
import { INTERNAL_LINKS } from '@/constants/links'
import type { LandingColors } from '@/constants/landing-content/types'

// Instrumentos - deben coincidir con los valores en HubSpot
const INSTRUMENTS = [
  { value: 'Piano', label: 'Piano' },
  { value: 'Guitar', label: 'Guitarra' },
  { value: 'Violin', label: 'Violín' },
  { value: 'Drums', label: 'Batería' },
  { value: 'Voice', label: 'Voz' },
  { value: 'Bass', label: 'Bajo' },
  { value: 'Saxophone', label: 'Saxofón' },
  { value: 'Trumpet', label: 'Trompeta' },
  { value: 'Flute', label: 'Flauta' },
  { value: 'Other', label: 'Otro' },
]

const LEVELS = [
  { value: 'principiante', label: 'Principiante', description: 'Estoy empezando' },
  { value: 'intermedio', label: 'Intermedio', description: 'Llevo un tiempo tocando' },
  { value: 'avanzado', label: 'Avanzado', description: 'Tengo experiencia en bandas/jams' },
]

interface WaitlistFormData {
  email: string
  firstname: string
  instrument: string
  level: string
  city?: string
}

interface WaitlistModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  colors: LandingColors
  source?: string
  /** Email pre-filled from hero inline form */
  initialEmail?: string
  initialCity?: string
  className?: string
  returnFocusRef?: { current: HTMLElement | null }
}

export function WaitlistModal({
  open,
  onOpenChange,
  colors,
  source = 'jams_hero',
  initialEmail = '',
  initialCity = '',
  className = '',
  returnFocusRef
}: WaitlistModalProps) {
  const [formData, setFormData] = useState<Partial<WaitlistFormData>>({
    email: initialEmail,
    city: initialCity
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitState, setSubmitState] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [consentMarketing, setConsentMarketing] = useState(false)

  // Synchronize only a changed prefill prop, before rendering the form.
  const [previousInitialEmail, setPreviousInitialEmail] = useState(initialEmail)
  if (initialEmail !== previousInitialEmail) {
    setPreviousInitialEmail(initialEmail)
    if (initialEmail) setFormData(prev => ({ ...prev, email: initialEmail }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.email || !formData.firstname || !formData.instrument || !formData.level || !consentMarketing) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage('')

    const result = await submitLeadB2C({
      email: formData.email,
      firstname: formData.firstname,
      instrument: formData.instrument,
      level: formData.level,
      city: formData.city,
      source,
      marketing_consent: consentMarketing,
    })

    setIsSubmitting(false)

    if (result.success) {
      trackWaitlistSignup(source, formData.instrument, formData.level)
      setSubmitState('success')
    } else {
      setSubmitState('error')
      setErrorMessage(result.error || 'Error desconocido')
    }
  }

  const isFormValid = formData.email && formData.firstname && formData.instrument && formData.level && consentMarketing

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-xl lg:max-w-2xl font-landing max-h-[90dvh] overflow-y-auto ${className}`} onCloseAutoFocus={event => {
        if (returnFocusRef?.current?.isConnected) {
          event.preventDefault()
          returnFocusRef.current.focus({ preventScroll: true })
        }
      }}>
        <AnimatePresence mode="wait">
          {submitState === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center justify-center py-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 200, delay: 0.1 }}
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                style={{ backgroundColor: `${colors.primary}15` }}
              >
                <CheckCircle2 className="w-8 h-8" style={{ color: colors.primary }} />
              </motion.div>
              <DialogTitle className="text-xl font-bold text-gray-900 mb-2 font-landing">
                Ya estás en la lista
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Te avisaremos cuando puedas acceder a la beta. Empezamos en Madrid.
              </DialogDescription>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <DialogHeader>
                <DialogTitle className="text-center text-2xl font-landing font-bold tracking-tight text-gray-900">
                  Apúntate a la beta de Jamsom Music
                </DialogTitle>
                <DialogDescription className="text-center text-base text-gray-600">
                  Empezamos en Madrid. Cuéntanos qué tocas y te avisaremos cuando puedas acceder.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                {/* Nombre */}
                <div className="space-y-2">
                  <label htmlFor="waitlist-name" className="text-sm font-medium text-gray-700">
                    Nombre
                  </label>
                  <Input
                    id="waitlist-name"
                    type="text"
                    autoComplete="given-name"
                    placeholder="Tu nombre"
                    value={formData.firstname || ''}
                    onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                {/* Email (pre-filled, editable) */}
                <div className="space-y-2">
                  <label htmlFor="waitlist-email" className="text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <Input
                    id="waitlist-email"
                    type="email"
                    autoComplete="email"
                    placeholder="tu@email.com"
                    value={formData.email || ''}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="h-11"
                    required
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  {/* Instrumento */}
                  <div className="space-y-2">
                    <label htmlFor="waitlist-instrument" className="text-sm font-medium text-gray-700">
                      ¿Qué instrumento tocas?
                    </label>
                    <Select
                      value={formData.instrument}
                      onValueChange={(value) => setFormData({ ...formData, instrument: value })}
                    >
                      <SelectTrigger id="waitlist-instrument" className="h-11">
                        <SelectValue placeholder="Elige tu instrumento..." />
                      </SelectTrigger>
                      <SelectContent position="popper" className="z-[9999]">
                        {INSTRUMENTS.map((inst) => (
                          <SelectItem key={inst.value} value={inst.value}>
                            {inst.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Nivel */}
                  <div className="space-y-2">
                    <label htmlFor="waitlist-level" className="text-sm font-medium text-gray-700">
                      ¿Cuál es tu nivel?
                    </label>
                    <Select
                      value={formData.level}
                      onValueChange={(value) => setFormData({ ...formData, level: value })}
                    >
                      <SelectTrigger id="waitlist-level" className="h-11">
                        <SelectValue placeholder="Elige tu nivel..." />
                      </SelectTrigger>
                      <SelectContent position="popper" className="z-[9999]">
                        {LEVELS.map((lvl) => (
                          <SelectItem key={lvl.value} value={lvl.value}>
                            <span className="flex flex-col">
                              <span>{lvl.label}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Ciudad (opcional) */}
                <div className="space-y-2">
                  <label htmlFor="waitlist-city" className="text-sm font-medium text-gray-700">
                    Ciudad <span className="text-gray-400 font-normal">(opcional)</span>
                  </label>
                  <Input
                    id="waitlist-city"
                    type="text"
                    autoComplete="address-level2"
                    placeholder="Madrid, Barcelona..."
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="h-11"
                  />
                </div>

                {/* Consentimiento de marketing (RGPD) — opt-in explícito, requerido */}
                <div className="flex items-start gap-3 pt-1">
                  <Checkbox
                    id="waitlist-consent"
                    checked={consentMarketing}
                    onCheckedChange={(checked) => setConsentMarketing(checked === true)}
                    className="mt-0.5"
                  />
                  <label
                    htmlFor="waitlist-consent"
                    className="text-xs text-gray-600 cursor-pointer leading-relaxed"
                  >
                    Acepto recibir novedades sobre jams en mi ciudad y la{' '}
                    <a href={INTERNAL_LINKS.privacidad} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline" style={{ color: colors.primary }}>
                      Política de privacidad
                    </a>{' '}
                    y los{' '}
                    <a href={INTERNAL_LINKS.terminos} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline" style={{ color: colors.primary }}>
                      Términos
                    </a>.
                  </label>
                </div>

                {/* Error message */}
                {submitState === 'error' && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    role="alert"
                    className="text-sm text-red-600 text-center bg-red-50 py-2 px-3 rounded-lg"
                  >
                    {errorMessage}
                  </motion.p>
                )}

                {/* Submit button */}
                <Button
                  type="submit"
                  disabled={!isFormValid || isSubmitting}
                  className="w-full h-12 text-base font-semibold rounded-xl shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{
                    backgroundColor: colors.primary,
                    boxShadow: isFormValid ? `0 8px 16px -4px ${colors.primary}40` : 'none',
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Apuntando...
                    </>
                  ) : (
                    'Apuntarme a la beta'
                  )}
                </Button>

                <p className="text-xs text-gray-400 text-center">
                  Sin spam. Puedes darte de baja cuando quieras.
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  )
}

export default WaitlistModal
